/**
 * External Imports
 */
import { FC, useEffect, useState } from 'react';

/**
 * Internal Imports
 */
import { BlueChip } from '../../../shared/chips/blueChip';
import { Dropdown } from '../../../shared/dropdowns';
import { useDebounceValue } from '../../../../utils/debouncers';
import { compareObjByStringKey } from '../../../../utils/helpers';

import { useLazyGetEmployeesBySearchQuery } from '../../../../redux/apis/Employees.api';

/**
 * Global Type Definition Imports
 */
import { Employee } from '../../../../config/interfaces';

/**
 * Style Imports
 */
import { ChipWrapper } from '../../feedbackModal/FeedbackModal.styles';
import { InputHeading, StyledDivider } from './EmployeeSearch.styles';

interface EmpSearchProps {
  chipVal?: string;
  multipleChipVals?: string[];
  showLabel?: boolean; // true by default
  val: string | string[];
  isMultipleRounderSelectionAllowed?: boolean;
  handleRemoveEmployee: (data: string) => void;
  setEmployeeSelection: (employee: Employee) => void;
}

const EmployeeSearch: FC<EmpSearchProps> = ({
  chipVal,
  multipleChipVals,
  showLabel,
  val,
  isMultipleRounderSelectionAllowed,
  setEmployeeSelection,
  handleRemoveEmployee
}) => {
  const [activeSearch, setActiveSearch] = useState('');
  const [query, setQuery] = useState('');
  const [isEmployeeSearchOpen, setIsEmployeeSearchOpen] =
    useState<boolean>(false);
  const [showLabelState] = useState<boolean>(showLabel !== false);

  const dbEmpSearchTerm = useDebounceValue(query, 250);

  const [
    getEmployeesBySearch,
    {
      data: employeesBySearchData
      // isError: isGetEmployeesBySearchError,
      // isFetching: isGetEmployeesBySearchFetching,
      // isLoading: isGetEmployeesBySearchLoading
    }
  ] = useLazyGetEmployeesBySearchQuery();

  // watching for debounced search terms before dispatching searches
  useEffect(() => {
    getEmployeesBySearch(dbEmpSearchTerm);
  }, [dbEmpSearchTerm]);

  const handleSearching = (e: CustomEvent<any>) => {
    const target = e.target as HTMLInputElement;
    setQuery(target.value);
  };

  const selectEmployee = (emp: Employee) => {
    // setting selection for the modal it is in
    setEmployeeSelection(emp);
    setActiveSearch('');
    setQuery('');
  };

  return (
    <div id="Modal-Employee-Search">
      {showLabelState ? <InputHeading>Employee</InputHeading> : null}
      {chipVal && (
        <ChipWrapper onClick={() => handleRemoveEmployee('employee')}>
          <BlueChip name={chipVal} />
        </ChipWrapper>
      )}
      {multipleChipVals &&
        multipleChipVals.map(chip => (
          <ChipWrapper key={chip} onClick={() => handleRemoveEmployee(chip)}>
            <BlueChip name={chip} />
          </ChipWrapper>
        ))}
      {(isMultipleRounderSelectionAllowed || !chipVal) && (
        <div className="position-relative">
          <Dropdown
            id="Modal-Employee-Search-Dropdown"
            focused={activeSearch === 'employee'}
            name="employee"
            open={isEmployeeSearchOpen}
            placeholder="Start typing name or 3-4 ID"
            query={query}
            typeAhead
            setOpen={setIsEmployeeSearchOpen}
            setTypeAhead={setActiveSearch}
            setTypeAheadChange={handleSearching}
          >
            {[...(employeesBySearchData ?? [])]
              .sort(compareObjByStringKey('lastName'))
              .sort(compareObjByStringKey('firstName'))
              .map((emp: Employee) => {
                return (
                  <div key={emp.employeeId}>
                    <div
                      id={`Modal-Employee-Search-Dropdown-Option-${emp.employeeId}`}
                      aria-selected={val?.includes(emp.employeeId)}
                      className={`dropdown-hover-item${
                        val?.includes(emp.employeeId) ? ' selected' : ''
                      }`}
                      role="option"
                      tabIndex={0}
                      onClick={() => selectEmployee(emp)}
                      onKeyDown={() => selectEmployee(emp)}
                    >
                      <div className="d-flex justify-content-between">
                        <p className="mt-1 mb-0">
                          {emp.firstName.toUpperCase()}{' '}
                          {emp.lastName.toUpperCase()}
                        </p>
                        <p className="mt-1 mb-0" style={{ fontSize: '14px' }}>
                          {emp.employeeId}
                        </p>
                      </div>
                      <p className="mt-1 mb-0" style={{ fontSize: '14px' }}>
                        {emp.facility || 'Unknown'}
                      </p>
                      <p className="mt-1 mb-0" style={{ fontSize: '14px' }}>
                        {emp.title}
                      </p>
                    </div>
                    {[...(employeesBySearchData ?? [])].length > 1 && (
                      <StyledDivider className="my-0" />
                    )}
                  </div>
                );
              })}
          </Dropdown>
        </div>
      )}
    </div>
  );
};

export default EmployeeSearch;
